import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { User, Mail, Lock, Wallet, LogIn, UserPlus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DeveloperLogin = () => {
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [loginForm, setLoginForm] = useState({
    username: '',
    password: ''
  });

  const [registerForm, setRegisterForm] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    walletAddress: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.username || !loginForm.password) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/auth/login`, loginForm);
      
      localStorage.setItem('dev_token', response.data.access_token);
      localStorage.setItem('dev_username', response.data.username);
      localStorage.setItem('dev_api_key', response.data.api_key);
      
      toast.success('Login successful!');
      navigate('/developer/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (registerForm.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/auth/register`, {
        username: registerForm.username,
        email: registerForm.email,
        password: registerForm.password,
        wallet_address: registerForm.walletAddress || null
      });
      
      toast.success('Registration successful! Please login.');
      setActiveTab('login');
      setLoginForm({ username: registerForm.username, password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full glass p-8 rounded-sm"
      >
        <div className="text-center mb-8">
          <h1 className="font-unbounded text-3xl font-bold text-primary mb-2">
            Developer Portal
          </h1>
          <p className="text-text-secondary">Deploy and manage smart contracts</p>
        </div>

        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('login')}
            className={`flex-1 py-3 rounded-sm font-bold transition-all duration-200 ${
              activeTab === 'login'
                ? 'bg-primary text-primary-foreground'
                : 'bg-background-subtle text-text-secondary hover:bg-background-card'
            }`}
            data-testid="login-tab"
          >
            <LogIn size={18} className="inline mr-2" />
            Login
          </button>
          <button
            onClick={() => setActiveTab('register')}
            className={`flex-1 py-3 rounded-sm font-bold transition-all duration-200 ${
              activeTab === 'register'
                ? 'bg-primary text-primary-foreground'
                : 'bg-background-subtle text-text-secondary hover:bg-background-card'
            }`}
            data-testid="register-tab"
          >
            <UserPlus size={18} className="inline mr-2" />
            Register
          </button>
        </div>

        {activeTab === 'login' ? (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="text-text-secondary text-sm mb-2 block flex items-center gap-2">
                <User size={16} />
                Username
              </label>
              <input
                type="text"
                value={loginForm.username}
                onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                className="w-full p-3 bg-background-subtle border border-border rounded-sm text-text-primary focus:border-primary focus:outline-none"
                placeholder="Enter username"
                data-testid="login-username"
              />
            </div>
            <div>
              <label className="text-text-secondary text-sm mb-2 block flex items-center gap-2">
                <Lock size={16} />
                Password
              </label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                className="w-full p-3 bg-background-subtle border border-border rounded-sm text-text-primary focus:border-primary focus:outline-none"
                placeholder="Enter password"
                data-testid="login-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-primary text-primary-foreground font-bold rounded-sm hover:bg-primary-hover transition-colors duration-200 disabled:opacity-50"
              data-testid="login-submit"
            >
              {loading ? 'Logging in...' : 'Login to Dashboard'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label className="text-text-secondary text-sm mb-2 block flex items-center gap-2">
                <User size={16} />
                Username *
              </label>
              <input
                type="text"
                value={registerForm.username}
                onChange={(e) => setRegisterForm({ ...registerForm, username: e.target.value })}
                className="w-full p-3 bg-background-subtle border border-border rounded-sm text-text-primary focus:border-primary focus:outline-none"
                placeholder="Choose username"
                data-testid="register-username"
              />
            </div>
            <div>
              <label className="text-text-secondary text-sm mb-2 block flex items-center gap-2">
                <Mail size={16} />
                Email *
              </label>
              <input
                type="email"
                value={registerForm.email}
                onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                className="w-full p-3 bg-background-subtle border border-border rounded-sm text-text-primary focus:border-primary focus:outline-none"
                placeholder="your@email.com"
                data-testid="register-email"
              />
            </div>
            <div>
              <label className="text-text-secondary text-sm mb-2 block flex items-center gap-2">
                <Lock size={16} />
                Password * (min 8 characters)
              </label>
              <input
                type="password"
                value={registerForm.password}
                onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                className="w-full p-3 bg-background-subtle border border-border rounded-sm text-text-primary focus:border-primary focus:outline-none"
                placeholder="Create password"
                data-testid="register-password"
              />
            </div>
            <div>
              <label className="text-text-secondary text-sm mb-2 block flex items-center gap-2">
                <Lock size={16} />
                Confirm Password *
              </label>
              <input
                type="password"
                value={registerForm.confirmPassword}
                onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                className="w-full p-3 bg-background-subtle border border-border rounded-sm text-text-primary focus:border-primary focus:outline-none"
                placeholder="Confirm password"
                data-testid="register-confirm-password"
              />
            </div>
            <div>
              <label className="text-text-secondary text-sm mb-2 block flex items-center gap-2">
                <Wallet size={16} />
                Wallet Address (optional)
              </label>
              <input
                type="text"
                value={registerForm.walletAddress}
                onChange={(e) => setRegisterForm({ ...registerForm, walletAddress: e.target.value })}
                className="w-full p-3 bg-background-subtle border border-border rounded-sm text-text-primary focus:border-primary focus:outline-none font-mono text-sm"
                placeholder="0x..."
                data-testid="register-wallet"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-secondary text-secondary-foreground font-bold rounded-sm hover:opacity-90 transition-opacity duration-200 disabled:opacity-50"
              data-testid="register-submit"
            >
              {loading ? 'Creating Account...' : 'Create Developer Account'}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
};

export default DeveloperLogin;
